import { DateTime } from "luxon";
import { TEmailTemplateContext } from "../codecs/EmailTemplateContext";
import { formatCurrencyFromPence } from "../../shared/src/util/numberFormatting";

// What happens to a token the case has no value for.
//
// "marker" leaves a visible [[Label]] in the draft. SendNewLegalEmail refuses a body that
// still contains one, so an unfilled field cannot reach a conveyancer - the alternative,
// interpolating "", silently sends "Account number:" with nothing after it.
//
// "omit" drops the token silently, for parts of a sentence that read fine without them.
export type TTokenMissingPolicy = "marker" | "omit";

export type TEmailTemplateTokenDefinition = {
    label: string;
    resolve: (context: TEmailTemplateContext) => string;
    whenMissing: TTokenMissingPolicy;
};

const formatDate = (d: string | null): string =>
    d === null ? "" : DateTime.fromISO(d).toFormat("d LLLL yyyy");

export const EmailTemplateTokens: Record<string, TEmailTemplateTokenDefinition> = {
    client_names: {
        label: "Client name",
        resolve: (c) => c.client_names,
        whenMissing: "marker",
    },
    property_address: {
        label: "Property address",
        resolve: (c) => c.property_address,
        whenMissing: "marker",
    },
    title_numbers: {
        label: "Title number",
        resolve: (c) => c.title_numbers,
        whenMissing: "marker",
    },
    matter_id: {
        label: "Matter id",
        resolve: (c) => c.klyant_matter_id,
        whenMissing: "marker",
    },
    case_handler_name: {
        label: "Case handler",
        resolve: (c) => c.case_handler_name,
        whenMissing: "marker",
    },
    completion_date: {
        label: "Completion date",
        resolve: (c) => formatDate(c.expected_completion_date),
        whenMissing: "marker",
    },
    // The lender on the existing charge, off the title register. Free text, and NOT the
    // same lender as mortgage_lender_* below.
    charge_beneficiary: {
        label: "Lender",
        resolve: (c) => c.charge_beneficiary,
        whenMissing: "marker",
    },
    charge_account_number: {
        label: "Account number",
        resolve: (c) => c.charge_account_number,
        whenMissing: "marker",
    },
    // The purchase/remortgage lender and our panel number with them. Only meaningful when
    // we act for that lender, so a sale has neither.
    mortgage_lender_name: {
        label: "Mortgage lender",
        resolve: (c) => c.mortgage_lender_name,
        whenMissing: "marker",
    },
    mortgage_lender_panel_number: {
        label: "Panel number",
        resolve: (c) => c.mortgage_lender_panel_number,
        whenMissing: "marker",
    },
    mortgage_offer_date: {
        label: "Mortgage offer date",
        resolve: (c) => formatDate(c.mortgage_offer_date),
        whenMissing: "marker",
    },
    price: {
        label: "Price",
        resolve: (c) => c.price_pence === null ? "" : formatCurrencyFromPence(c.price_pence),
        whenMissing: "marker",
    },
};

export const isKnownToken = (name: string): boolean =>
    Object.prototype.hasOwnProperty.call(EmailTemplateTokens, name);

const TOKEN_PATTERN = /\{\{\s*([a-z_]+)\s*\}\}/g;

// Every {{token}} a body references, in order of appearance. Used to validate a template
// at authoring time rather than discovering a typo when someone tries to send.
export const getReferencedTokens = (body: string): Array<string> =>
    [...body.matchAll(TOKEN_PATTERN)].map(([, name]) => name);

export const getUnknownTokens = (body: string): Array<string> =>
    getReferencedTokens(body).filter((name) => !isKnownToken(name));

export const MARKER_PATTERN = /\[\[[^\]]+\]\]/g;

export const getUnresolvedMarkers = (body: string): Array<string> =>
    body.match(MARKER_PATTERN) ?? [];

// An unknown token is left verbatim rather than blanked: a template referencing {{typo}}
// should look obviously broken to whoever inserted it, not quietly lose a sentence.
export const resolveTemplateTokens = (body: string, context: TEmailTemplateContext | null): string =>
    body.replace(TOKEN_PATTERN, (whole, name: string) => {
        const token = EmailTemplateTokens[name];
        if (token === undefined) {
            return whole;
        }
        if (context === null) {
            return `[[${token.label}]]`;
        }
        const value = token.resolve(context);
        if (value !== "") {
            return value;
        }
        return token.whenMissing === "omit" ? "" : `[[${token.label}]]`;
    });
